import * as React from 'react';
import { connect } from 'react-redux';
import { flow, isUndefined, each } from 'lodash';
import { Col, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { IBrandingActions, IBrandingState, II18nMessages } from '../../services/branding/models';
import { Route, RouteComponentProps, Switch, withRouter, Redirect } from 'react-router';
import { IStore } from '../../redux/IStore';
import { actions as brandingActions } from '../../services/branding/reducer';
import { bindActionCreators } from 'redux';
import { change } from 'redux-form';
import { IChannel, ICustomer, IPolicyApplication, IProduct, IStep } from '../../data/api/models';
import { withJob } from 'react-jobs';
import { IQuoteActions, IQuoteState as IQuoteServiceState } from '../../services/quote/models';
import { actions as quoteActions } from '../../services/quote/reducer';
import { push } from 'react-router-redux';
import FaInfoCircle from 'react-icons/fa/info-circle';
import * as Commonmark from 'react-commonmark';
import { IAuthState } from '../../services/auth/models';
import Tracking from './components/Tracking';
import { IReactStep } from './steps';
import RetrieveAggregatorQuote from './components/RetrieveAggregatorQuote';
import QuoteSidebar from './components/QuoteSidebar';
import Loading from '../../components/Loading';
import FaInfo from 'react-icons/fa/info';
import FaClose from 'react-icons/fa/close';
import FaQuestionCircle from 'react-icons/fa/question-circle';
import { AutoAffix } from 'react-overlays';
import PageNotFound from '../Content/scenes/PageNotFound';
import PaymentModal from './components/PaymentModal';

interface IQuoteProps extends RouteComponentProps<any> {
  auth: IAuthState;
  i18n: II18nMessages;
  steps: IReactStep[];
  quote: IQuoteServiceState;
  channel: IChannel;
  customer: ICustomer;
  branding: IBrandingState;
  dispatch: any;
  actions: {
    branding: IBrandingActions,
    quote: IQuoteActions;
    push: (route: string) => void;
  };
}

interface IQuoteState {
  showModal: boolean;
  values: any;
  isMobileView: boolean;
  transactionId: string | null;
  paymentUrl: string;
}

class Quote extends React.Component<IQuoteProps, IQuoteState> {
  public state = {
    showModal: false,
    values: null,
    transactionId: null,
    isMobileView: false,
    paymentUrl: '',
  };

  public componentWillMount() {
    const { actions, quote, branding } = this.props;
    const product = actions.branding.getProduct();

    if (process.env.BROWSER) {
      if (window.innerWidth < 500) {
        this.setState({
          isMobileView: true,
        });
      }
    }

    if (!quote.initialised) {
      actions.quote.initFromProduct(product, quote, branding);
    }
  }

  public openModal = (values, transactionId, paymentUrl) => {
    this.setState({
      showModal: true,
      transactionId,
      values,
      paymentUrl,
    });
  }

  public closePaymentModal = () => {
    this.setState({ showModal: false, transactionId: null, paymentUrl: null, values: null });
  }

  private getCurrentLocation = () => {
    const { location, steps } = this.props;

    let currentLocationIndex = null;

    steps.forEach((step, key) => {
      if (step.route === location.pathname) {
        currentLocationIndex = key;
      }
    });

    return currentLocationIndex;
  }

  private safePush = (to: string) => {
    const { location, actions } = this.props;
    if (location && location.pathname !== to) {
      actions.push(to);
    }
  }

  private scrollToTop = () => {
    if (process.env.BROWSER) {
      window.scrollTo(0, 0);
    }
  }

  private handleBack = () => {
    const { steps, actions } = this.props;

    const currentLocation = this.getCurrentLocation();

    this.safePush(steps[ currentLocation - 1 ].route);
    this.scrollToTop();
  }

  private handleSubmit = (formValues: IPolicyApplication) => {
    const { steps, actions, branding, channel } = this.props;
    const currentLocation = this.getCurrentLocation();
    const currentStep = steps [ currentLocation ];
    const nextStep = steps[ currentLocation + 1 ];
    const isFinalStep = nextStep.key === 'complete';

    if (!isFinalStep) {
      formValues.steps.push(nextStep.key);
      actions.quote.addStepToWizard(nextStep.key);
    } else {
      formValues.purchaseChannel =
        branding.channel ? branding.channel.key : channel.key;
    }

    const handleNextStep = this.handleNextStep(formValues, nextStep);

    if (isUndefined(currentStep.key) || !currentStep.saveApplication) {
      actions.quote.addStepToWizard(nextStep.key, false);
      handleNextStep();
      return;
    }

    if (nextStep.key === 'schemeChoice') {
      formValues.calculateAllSchemes = true;
    }

    const calculate = currentStep.calculatePremium ? currentStep.calculatePremium : isFinalStep;
    const patch = currentStep.saveApplicationWithPatch;

    return actions.quote.submitForm(formValues, calculate, false, patch)
      .then(handleNextStep);
  }

  private handleNextStep = (formValues, nextStep: IStep) => () => {
    const { actions, dispatch } = this.props;

    if (nextStep.key === 'complete') {
      return actions.quote.purchase(formValues)
        .then((data) => {
          actions.quote.complete(data.policies[ 0 ]);
          actions.quote.addStepToWizard('complete', false);

          this.safePush(nextStep.route);

          this.scrollToTop();
        })
        .catch((error) => {
          let rethrow = true;
          let message = null;

          each(error.response.data.errors, (e: any) => {
            if (e.id !== 'redirect-to-provider') {
              message = e.detail;
              return;
            }
            this.openModal(formValues, e.data.transactionId, e.data.url);
            rethrow = false;
          });

          if (!rethrow) {
            return;
          }

          if (message) {
            actions.quote.error(message);
            this.scrollToTop();
          }

          throw error;
        });
    }

    this.safePush(nextStep.route);
    this.scrollToTop();

    const values = { ...formValues };

    if (nextStep.calculatePremium) {
      if (nextStep.key === 'payment') {
        values.emailSend = true;
        dispatch(change('quote', 'emailSend', true));
      }
      Promise.resolve(actions.quote.calculate(values)).then((rs) => {
        dispatch(change('quote', 'signposted', rs));
        dispatch(change('quote', 'emailSend', false));
      });
    }
  }

  private renderStepRoutes = (product: IProduct) => {
    const { steps, quote, i18n, channel, location, actions, dispatch } = this.props;

    return steps.map((step, key) => {
      const { route: routePath } = step;
      const exact = routePath === '/';

      const onBack = key > 0 ? this.handleBack : null;

      return (
        <Route key={key} exact={exact} path={routePath}>
          <step.component
            product={product}
            initialValues={quote.application}
            destinations={quote.destinations}
            occupations={quote.occupations}
            industries={quote.industries}
            channel={channel}
            i18n={i18n}
            onSubmit={this.handleSubmit}
            onBack={onBack}
            renderDefinition={this.renderDefinition(product)}
            isMobileView={this.state.isMobileView}
            {...this.props}
          />
        </Route>
      );
    });
  }

  private fireEmail = () => {
    const { dispatch } = this.props;

    dispatch(change('quote', 'emailSend', true));
    dispatch(change('quote', 'generateDocuments', true));
  }

  private transformLinks = (url) => {
    const { actions } = this.props;
    const product = actions.branding.getProduct();

    // ActivitiesSelect Urls
    const activitiesUrls = {
      DIA: 'http://www.travelinsurance.diabetes.co.uk/activities',
      LSB: 'http://www.longstaycover.co.uk/activities',
    };

    if (url === 'activities_pack' && activitiesUrls[product.productCode]) {
      return activitiesUrls[product.productCode];
    }

    return url;
  }

  private onClose = () => {
    document.body.click();
  }

  private renderDefinition = (product: IProduct) => (name: string, defName?: string, icon?: string) => {
    const { isMobileView } = this.state;

    let definition = product.metadata.definitions[ name ];

    if (defName) {
      definition = product.metadata.definitions[ defName ];
    }

    if (!definition) {
      return null;
    }

    const popover = (
      <Popover
        id={`popover-${name}`}
        onClick={this.onClose}
      >
        <div className="close-container">
          <div className="close-tooltip">
            <FaClose/>
          </div>
          <Commonmark
            source={definition}
            linkTarget="_blank"
            transformLinkUri={this.transformLinks}
          />
        </div>
      </Popover>
    );

    const { channel } = this.props;
    return (
      <OverlayTrigger
        trigger="click"
        rootClose={true}
        overlay={popover}
        placement={isMobileView ? 'bottom' : 'right'}
        positionLeft={200}
        positionTop={50}
      >
        {channel.key === 'camc-b2c' ? (
            <FaQuestionCircle className="info-btn-helper"/>
        ) : (
            icon === 'FaInfo' ? <FaInfo className="info-btn-helper"/> : <FaInfoCircle className="info-btn"/>
        )}
      </OverlayTrigger>
    );
  }

  private renderPaymentModal = () => {
    return (
        <PaymentModal
            transactionId={this.state.transactionId}
            paymentUrl={this.state.paymentUrl}
            actions={this.props.actions}
            showModal={this.state.showModal}
            onClose={this.closePaymentModal}
        />
    );
  }

  public render() {
    const { actions, steps, quote, location, branding } = this.props;
    const product = actions.branding.getProduct();
    const currentLocation = this.getCurrentLocation() || 0;
    const hasAggError = quote.aggError;
    const isAgg = branding.channel.channelType === 'AGG';
    if (
        !isAgg &&
        quote.application.steps &&
        quote.application.steps.indexOf(steps[currentLocation].key) === -1
    ) {
      const firstStepRoute = steps[0].route;
      if (location.pathname !== firstStepRoute) {
        return <Redirect to={firstStepRoute} />;
      }
    }
    let showSideBar = true;
    if (null === this.getCurrentLocation()) {
      showSideBar = false;
    }

    if (location) {
      if (location.pathname.match(/(privacy|cookies)/)) {
        return null;
      }

      if (location.pathname === '/') {
        const firstStepRoute = steps[0].route;
        this.safePush(firstStepRoute);
      }
    }

    if (hasAggError) {
      return (
        <div className="internal-page">
          <div className="container">
            <div className="agg-error-container">
              <h1>Thank you for choosing Free Spirit through <strong>{quote.aggName}</strong></h1>
              <h3>We have recently upgraded our system, please re-quote by clicking the button below</h3>
              <a href={quote.redirectLink} className="btn btn-primary btn-lg">
                Quote
              </a>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="internal-page">
        <Tracking/>
        <Route exact={true} path="/aggregator/:id" component={RetrieveAggregatorQuote}/>
        <div className="container">
          <Row>
            <Col md={showSideBar ? 9 : 12}>
              <div className="quote-content-container">
                <Switch>
                  {this.renderStepRoutes(product)}
                  <Route path="*" component={PageNotFound} />
                </Switch>
              </div>
            </Col>
            {showSideBar && (
              <div>
                <Col md={3} className="hide-mobile-sidebar">
                  <AutoAffix viewportOffsetTop={0} offsetTop={15} container={this}>
                    {/* This needs to have a div intermediary otherwise it doesn't work */}
                    <div>
                      <QuoteSidebar product={product} fireEmail={this.fireEmail} />
                    </div>
                  </AutoAffix>
                </Col>
                <Col md={3} className="show-desktop-sidebar">
                    <div>
                      <QuoteSidebar product={product} fireEmail={this.fireEmail} />
                    </div>
                </Col>
              </div>
            )}
          </Row>
        </div>

        {this.state.showModal && this.renderPaymentModal()}
      </div>
    );
  }
}

export default flow([
  withJob({
    work: (props: IQuoteProps) => {
      const promises = [];

      if (props.quote.destinations.length === 0) {
        promises.push(props.actions.quote.loadDestinations());
      }

      return Promise.all(promises);
    },
    LoadingComponent: (props) => <Loading/>,
    ErrorComponent: ({ error }) => {
      console.error(error);

      return (
        <div>
          {error.message}
        </div>
      );
    },
  }),
  connect(
    (state: IStore) => ({
      isDrawerOpened: state.header.isDrawerOpened,
      i18n: state.branding.i18n,
      quote: state.quote,
      auth: state.auth,
      branding: state.branding,
    }),
    (dispatch) => ({
      dispatch,
      actions: {
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
  withRouter,
])(Quote);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/index.tsx